import { ComplexValue, SubTable, FieldValue, Value } from 'app/types-shared';
import { BowtieCase, WireChecklistEvaluationData } from '../types';

interface Evaluation {
  no: number;
  evaluation: ComplexValue<number>;
}

const getFirstFieldValue = (field: FieldValue) => field && field.values && field.values[0] || {} as Value;

/**
 * Returns the list of evalutions for single checklist element
 *
 * @param subtable 
 */
const getChecklistItemEvaluation = (subtable: SubTable) => {

  const checklistInspections = subtable && subtable.subTables && subtable.subTables.TblChecklistBarrierInspView || [];

  return checklistInspections
    .map(inspection => {

      const noOfInspectionsField = inspection.fields && inspection.fields.CheckListBarrInspNoOfInspect;
      const noOfInspections = getFirstFieldValue(noOfInspectionsField).v as number || 0;

      const evaluationField = inspection.fields && inspection.fields.CheckListBarrInspEvaluation;
      const evaluation = getFirstFieldValue(evaluationField);

      return {
        no: noOfInspections,
        evaluation: {
          value: evaluation.v,
          description: evaluation.d,
          backgroundColor: evaluation.b,
          foregroundColor: evaluation.f,
          icon: evaluation.i
        }
      } as Evaluation;
    })
    .filter(evaluation => evaluation.no > 0);
};

/**
 * Merges two collections of evaluations and merges data with same value
 *
 * @param firstCollection 
 * @param secondCollection 
 */
const mergeEvaluations = (firstCollection: Evaluation[], secondCollection: Evaluation[]) => {

  return secondCollection && secondCollection.length !== 0
    ? [
      ...firstCollection.filter(
        firstItem => !secondCollection.some(
          secondItem => secondItem.evaluation.value === firstItem.evaluation.value
        )),
      ...secondCollection.map(secondItem => {

        const curentValue = firstCollection.find(firstItem => firstItem.evaluation.value === secondItem.evaluation.value);
        return curentValue
          ? {
            evaluation: curentValue.evaluation,
            no: curentValue.no + secondItem.no
          }
          : secondItem;
      })
    ]
    : firstCollection;
};

/**
 * Finds evaluations from given checklist
 *
 * @param subtable 
 */
const getChecklistEvaluation = (subtable: SubTable) => {

  const checklist = subtable && subtable.subTables && subtable.subTables.TblCheckList || [];

  return checklist.reduce((result, item) => mergeEvaluations(result, getChecklistItemEvaluation(item)), [] as Evaluation[]);
};

const normalizeEvaluationOutput = (evaluationItem: Evaluation): ComplexValue<number> => ({
  ...evaluationItem.evaluation,
  description: evaluationItem.evaluation.description
    ? `${evaluationItem.evaluation.description} (${evaluationItem.no})`
    : `(${evaluationItem.no})`
});

/**
 * Gets inspections evaluations for given case
 *
 * @param case 
 */
export const getInspections = (caseData: BowtieCase): ComplexValue<number>[] => {

  const checkListCategory = caseData && caseData.subTables && caseData.subTables.TblCheckListCategory || [];

  return checkListCategory
    .reduce((result, item) => mergeEvaluations(result, getChecklistEvaluation(item)), [] as Evaluation[])
    .map(normalizeEvaluationOutput)
    .sort((a, b) => a.value - b.value);
};

/**
 * Gets inspections evaluations for given cases
 *
 * @param cases 
 */
export const getInspectionEvaluations = (cases: BowtieCase[]) => cases.reduce((result, caseData) => {

  const evaluation = getInspections(caseData);

  return evaluation && evaluation.length
    ? {
      ...result,
      [caseData.caseNumber]: evaluation
    }
    : result;
}, {} as { [caseNumber: number]: ComplexValue<number>[] });

/**
 * Gets inspections evaluations from wire checklist evaluation endpoint data
 *
 * @param cases 
 */
export const mapChecklistEvaluation = (
  data: WireChecklistEvaluationData
): ComplexValue<number>[] => {

  return data?.checkListItems
    ?.map(
      checkListItem => checkListItem?.evaluations
        ?.map(
          evaluationItem => ({
            no: evaluationItem.count,
            evaluation: {
              value: evaluationItem.evaluation?.v,
              description: evaluationItem.evaluation?.d,
              backgroundColor: evaluationItem.evaluation?.b,
              foregroundColor: evaluationItem.evaluation?.f,
              icon: evaluationItem.evaluation?.i
            }
          } as Evaluation)
        )
        ?.filter(evaluation => evaluation.no > 0)
    )
    ?.reduce(mergeEvaluations, [])
    ?.map(normalizeEvaluationOutput)
    ?.sort((first, second) => first.value - second.value) ?? [];
};
